import { useEffect, useState, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Plus, Pencil, Trash2, Languages, Sparkles, ExternalLink, Eye, EyeOff, FileText } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export const BlogManager = ({ candles = [] }) => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [genCandle, setGenCandle] = useState("");
  const [generating, setGenerating] = useState(false);
  const [editing, setEditing] = useState(null); // full post
  const [form, setForm] = useState({ title: "", excerpt: "", body: "", slug: "" });
  const [savingEdit, setSavingEdit] = useState(false);

  const load = useCallback(async () => {
    try { const r = await api.get("/admin/blog"); setPosts(r.data || []); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const generate = async () => {
    if (!genCandle) return toast.error("Choisissez une fiche");
    setGenerating(true);
    try {
      await api.post(`/admin/blog/generate-from-candle/${genCandle}`);
      toast.success("Article généré par l'IA ✨");
      setGenCandle("");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur de génération"); }
    finally { setGenerating(false); }
  };

  const togglePublish = async (p) => {
    setBusyId(p.id);
    try {
      await api.patch(`/admin/blog/${p.id}`, { status: p.status === "published" ? "draft" : "published" });
      load();
    } catch { toast.error("Erreur"); } finally { setBusyId(null); }
  };

  const translate = async (p) => {
    setBusyId(p.id);
    toast.info("Traduction en cours dans les 12 langues…");
    try {
      const r = await api.post(`/admin/blog/${p.id}/translate`);
      toast.success(`Traduit : ${(r.data.translated || []).length} langue(s)`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur de traduction"); }
    finally { setBusyId(null); }
  };

  const del = async (p) => {
    if (!window.confirm(`Supprimer l'article « ${p.title} » ?`)) return;
    setBusyId(p.id);
    try { await api.delete(`/admin/blog/${p.id}`); load(); } catch { toast.error("Erreur"); } finally { setBusyId(null); }
  };

  const openEdit = async (p) => {
    try {
      const r = await api.get(`/admin/blog/${p.id}`);
      const fr = (r.data.content || {}).fr || {};
      setEditing(r.data);
      setForm({ title: fr.title || "", excerpt: fr.excerpt || "", body: fr.body || "", slug: r.data.slug || "" });
    } catch { toast.error("Erreur"); }
  };

  const saveEdit = async () => {
    setSavingEdit(true);
    try {
      await api.patch(`/admin/blog/${editing.id}`, { lang: "fr", title: form.title, excerpt: form.excerpt, body: form.body, slug: form.slug });
      toast.success("Article enregistré");
      setEditing(null);
      load();
    } catch { toast.error("Erreur"); } finally { setSavingEdit(false); }
  };

  const createNew = async () => {
    try {
      const r = await api.post("/admin/blog", { title: "Nouvel article", status: "draft" });
      load();
      openEdit({ id: r.data.id });
    } catch { toast.error("Erreur"); }
  };

  const publishedIds = new Set(posts.map((p) => p.candle_id).filter(Boolean));
  const fmtD = (d) => (d ? String(d).slice(0, 10).split("-").reverse().join("/") : "");
  const namedCandles = candles
    .filter((c) => c.deceased_name && !publishedIds.has(c.id))
    .sort((a, b) => {
      const da = String(a.death_date || "").slice(0, 10);
      const db = String(b.death_date || "").slice(0, 10);
      if (!da && !db) return 0;
      if (!da) return 1;
      if (!db) return -1;
      return db.localeCompare(da);
    });

  return (
    <div className="space-y-5" data-testid="blog-manager">
      <div className="rounded-3xl border border-sky-400/20 bg-sky-500/[0.05] p-5">
        <div className="flex items-center gap-2 text-sky-300"><FileText size={18} /><h2 className="text-lg font-semibold">Blog SEO</h2></div>
        <p className="mt-1 text-sm text-muted-foreground">Chaque nouvelle flamme payée génère automatiquement un article optimisé pour Google & les IA. Gérez, enrichissez et traduisez vos articles. Les articles publiés sont publics (indexables) mais absents du menu utilisateur.</p>
      </div>

      {/* Générer depuis une fiche */}
      <div className="rounded-2xl border border-white/10 bg-card/40 p-5">
        <Label className="flex items-center gap-2 text-fuchsia-300"><Sparkles size={15} /> Générer un article depuis une fiche (IA)</Label>
        <div className="mt-2 flex flex-col gap-2 sm:flex-row">
          <Select value={genCandle} onValueChange={setGenCandle}>
            <SelectTrigger className="sm:flex-1" data-testid="blog-gen-candle-select"><SelectValue placeholder="Choisir une flamme…" /></SelectTrigger>
            <SelectContent>
              {namedCandles.map((c) => (
                <SelectItem key={c.id} value={c.id}>
                  <span className="flex items-center gap-2">
                    {c.flag && <img src={c.flag} alt="" className="h-3.5 w-5 shrink-0 rounded-[2px] object-cover" />}
                    <span>{c.deceased_name}</span>
                    {c.death_date && <span className="text-xs text-muted-foreground">· {fmtD(c.death_date)}</span>}
                  </span>
                </SelectItem>
              ))}
              {namedCandles.length === 0 && (
                <div className="px-3 py-2 text-sm text-muted-foreground">Toutes les flammes ont déjà un article</div>
              )}
            </SelectContent>
          </Select>
          <Button onClick={generate} disabled={generating} className="rounded-full" data-testid="blog-generate-btn">
            {generating ? <Loader2 size={15} className="mr-1.5 animate-spin" /> : <Sparkles size={15} className="mr-1.5" />} Générer
          </Button>
          <Button onClick={createNew} variant="outline" className="rounded-full border-white/15" data-testid="blog-new-btn"><Plus size={15} className="mr-1.5" /> Article vierge</Button>
        </div>
      </div>

      {/* Liste */}
      {loading ? (
        <div className="flex justify-center py-12"><Loader2 className="animate-spin text-sky-400" /></div>
      ) : posts.length === 0 ? (
        <p className="py-12 text-center text-sm text-muted-foreground" data-testid="blog-manager-empty">Aucun article. Générez-en un depuis une fiche.</p>
      ) : (
        <div className="space-y-3" data-testid="blog-manager-list">
          {posts.map((p) => (
            <div key={p.id} className="flex items-center gap-3 rounded-2xl border border-white/10 bg-card/40 p-3" data-testid={`blog-row-${p.slug}`}>
              {p.cover_image_url && <img src={`${BACKEND}${p.cover_image_url}`} alt="" className="h-14 w-20 shrink-0 rounded-lg object-cover" />}
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <span className={`rounded-full px-2 py-0.5 text-[10px] ${p.status === "published" ? "bg-emerald-500/15 text-emerald-300" : "bg-white/10 text-muted-foreground"}`}>{p.status === "published" ? "Publié" : "Brouillon"}</span>
                  {p.auto_generated && <span className="rounded-full bg-fuchsia-500/15 px-2 py-0.5 text-[10px] text-fuchsia-300">IA</span>}
                  <span className="text-[10px] text-muted-foreground">{(p.available_langs || []).length} langue(s)</span>
                </div>
                <p className="mt-1 truncate text-sm font-medium">{p.title}</p>
              </div>
              <div className="flex shrink-0 items-center gap-1">
                <Button size="icon" variant="ghost" className="h-8 w-8" title="Éditer" onClick={() => openEdit(p)} data-testid={`blog-edit-${p.slug}`}><Pencil size={15} /></Button>
                <Button size="icon" variant="ghost" className="h-8 w-8" title="Traduire (12 langues)" onClick={() => translate(p)} disabled={busyId === p.id} data-testid={`blog-translate-${p.slug}`}>{busyId === p.id ? <Loader2 size={15} className="animate-spin" /> : <Languages size={15} />}</Button>
                <Button size="icon" variant="ghost" className="h-8 w-8" title={p.status === "published" ? "Dépublier" : "Publier"} onClick={() => togglePublish(p)} disabled={busyId === p.id} data-testid={`blog-publish-${p.slug}`}>{p.status === "published" ? <EyeOff size={15} /> : <Eye size={15} />}</Button>
                <a href={`${BACKEND}/blog/${p.slug}`} target="_blank" rel="noopener noreferrer" className="inline-flex h-8 w-8 items-center justify-center rounded-md text-muted-foreground hover:bg-white/10" title="Voir"><ExternalLink size={15} /></a>
                <Button size="icon" variant="ghost" className="h-8 w-8 text-destructive" title="Supprimer" onClick={() => del(p)} disabled={busyId === p.id} data-testid={`blog-delete-${p.slug}`}><Trash2 size={15} /></Button>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto" data-testid="blog-edit-dialog">
          <DialogHeader><DialogTitle>Éditer l'article (français)</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Titre</Label><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="blog-edit-title" /></div>
            <div><Label>Slug (URL)</Label><Input value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value })} data-testid="blog-edit-slug" /></div>
            <div><Label>Résumé (meta description)</Label><Textarea rows={2} value={form.excerpt} onChange={(e) => setForm({ ...form, excerpt: e.target.value })} data-testid="blog-edit-excerpt" /></div>
            <div><Label>Contenu (HTML)</Label><Textarea rows={14} value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} className="font-mono text-xs" data-testid="blog-edit-body" /></div>
            <p className="text-xs text-muted-foreground">Après avoir enregistré le texte français, utilisez le bouton « Traduire » (12 langues) dans la liste.</p>
            <Button onClick={saveEdit} disabled={savingEdit} className="w-full rounded-full" data-testid="blog-edit-save">{savingEdit ? <Loader2 size={16} className="animate-spin" /> : "Enregistrer"}</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default BlogManager;
